import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface ParsedRow {
  firstName: string;
  lastName: string;
  phone: string;
  company: string;
  email: string;
  notes: string;
}

interface ImportResult {
  imported: number;
  skipped: number;
  invalidPhones?: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  campaignId?: string;
}

function pick(row: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k] ?? row[k.toLowerCase()] ?? row[k.toUpperCase()];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

export function LeadImportModal({ open, onClose, campaignId }: Props) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<'pick' | 'preview' | 'done'>('pick');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importing, setImporting] = useState(false);

  function reset() {
    setStep('pick');
    setRows([]);
    setParseError(null);
    setImportResult(null);
    setImporting(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError(null);

    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]!];
    if (!sheet) {
      setParseError('Could not read the first sheet.');
      return;
    }
    const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });

    const parsed: ParsedRow[] = raw.map((r) => ({
      firstName: pick(r, 'firstName', 'first_name', 'First Name', 'Firstname'),
      lastName:  pick(r, 'lastName',  'last_name',  'Last Name',  'Lastname'),
      phone:     pick(r, 'phone', 'Phone', 'telephone', 'Telephone', 'mobile', 'Mobile'),
      company:   pick(r, 'company', 'Company', 'organisation', 'Organization'),
      email:     pick(r, 'email', 'Email'),
      notes:     pick(r, 'notes', 'Notes', 'note', 'Note', 'comments', 'Comments'),
    }));

    const valid = parsed.filter((r) => r.firstName && r.lastName && r.phone);
    if (valid.length === 0) {
      setParseError(
        'No valid rows found. The file must have firstName (or "First Name"), lastName, and phone columns.',
      );
      // reset file input so the user can re-select
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setRows(valid);
    setStep('preview');
  }

  async function handleImport() {
    setImporting(true);
    setParseError(null);
    const endpoint = campaignId
      ? `/campaigns/${campaignId}/import-csv`
      : '/leads/import-csv';
    try {
      const result = await api.post<ImportResult>(endpoint, { rows });
      setImportResult(result);
      setStep('done');
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      if (campaignId) {
        queryClient.invalidateQueries({ queryKey: ['campaign-leads', campaignId] });
      }
    } catch (err: unknown) {
      setParseError(
        (err as { detail?: { error?: string } })?.detail?.error ?? 'Import failed. Please try again.',
      );
    } finally {
      setImporting(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink-900/40 p-4">
      <div className="card w-full max-w-3xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink-900">Import Leads</h3>
          <button className="btn-ghost text-xs" onClick={handleClose}>✕</button>
        </div>

        {/* Step: pick */}
        {step === 'pick' && (
          <div className="space-y-3">
            <p className="text-sm text-ink-600">
              Upload an Excel (.xlsx / .xls) or CSV file. Required columns:{' '}
              <strong>firstName</strong>, <strong>lastName</strong>, <strong>phone</strong>.
              Optional: company, email, <strong>notes</strong> (stored as a lead note).
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              className="btn-secondary"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose file…
            </button>
            {parseError && <p className="text-sm text-red-600">{parseError}</p>}
          </div>
        )}

        {/* Step: preview */}
        {step === 'preview' && (
          <div className="space-y-3">
            <p className="text-sm text-ink-600">
              <strong>{rows.length}</strong> valid row{rows.length !== 1 ? 's' : ''} ready to import.
              {rows.length > 50 && ' Showing first 50.'}
            </p>
            <div className="overflow-auto max-h-80 rounded border border-ink-200">
              <table className="w-full text-xs">
                <thead className="bg-ink-50 sticky top-0">
                  <tr>
                    {['First name', 'Last name', 'Phone', 'Company', 'Email', 'Notes'].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-medium uppercase tracking-wider text-ink-500">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 50).map((r, i) => (
                    <tr key={i} className="border-t border-ink-100">
                      <td className="px-3 py-1.5">{r.firstName}</td>
                      <td className="px-3 py-1.5">{r.lastName}</td>
                      <td className="px-3 py-1.5 font-mono">{r.phone}</td>
                      <td className="px-3 py-1.5 text-ink-500">{r.company || '—'}</td>
                      <td className="px-3 py-1.5 text-ink-500">{r.email || '—'}</td>
                      <td className="px-3 py-1.5 text-ink-500 max-w-[180px] truncate" title={r.notes}>
                        {r.notes || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {parseError && <p className="text-sm text-red-600">{parseError}</p>}
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => { setStep('pick'); setRows([]); }}>
                Back
              </button>
              <button className="btn-primary" disabled={importing} onClick={handleImport}>
                {importing ? 'Importing…' : `Import ${rows.length} leads`}
              </button>
            </div>
          </div>
        )}

        {/* Step: done */}
        {step === 'done' && importResult && (
          <div className="space-y-4">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm">
              <p className="font-semibold text-emerald-800">Import complete</p>
              <p className="text-emerald-700 mt-1">
                <strong>{importResult.imported}</strong> lead{importResult.imported !== 1 ? 's' : ''} imported
                {importResult.skipped > 0 && `, ${importResult.skipped} skipped (invalid phone)`}
                {importResult.invalidPhones != null && importResult.invalidPhones > 0 &&
                  `, ${importResult.invalidPhones} invalid phone numbers`}
                .
              </p>
            </div>
            <div className="flex justify-end">
              <button className="btn-primary" onClick={handleClose}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
